// A task's CTA is "Resolve with {agent}" — a handoff to the Maestro — until the task's own text says
// what the user is actually about to do. When it does, the button becomes that action instead, because
// sending someone to the Maestro to be told "open this link" is a detour around a link we already hold.
//
// Today there is exactly ONE recognized action: a browse session paused for a human. `Browse` pauses on
// the things only a person can clear (a login, a CAPTCHA, a payment), and the sub-agent is instructed to
// park its task with the live-view URL in the summary, verbatim — see the harness's
// `renderBrowseSystemMessage` ("[BROWSE NEEDS YOU]") and `ManageTask` set_status ACTION_NEEDED.
//
// Shared 1:1 with iOS `TaskKnownAction.swift` and Android `TaskKnownAction.kt`.

export type TaskKnownAction = { kind: 'browseSession'; url: string };

/** The task text this reads, in the order it reads it. */
export interface TaskActionText {
  summary?: string;
  currentAction?: string;
  subtitle?: string;
}

// The live-view link is the browse-session service's Wix proxy — path `browse-session-live`, carrying the
// real browser address NESTED after `?live=` (browse-session design-log #031), e.g.
//   https://www.wixapis.com/symphony/v1/browse-session-live/?live=https://www.browserbase.com/...?wss=...
//
// Recognition is deliberately two steps rather than one clever pattern, because the two things it has to
// get right pull against each other:
//  - Take the whole whitespace-delimited token. The nested address has its own `?` and `&`, so anything
//    that stops at a query delimiter hands over a truncated URL that opens the wrong page — the same
//    "must travel VERBATIM" rule the harness repeats to the agent.
//  - Identify it by a PATH SEGMENT, not the host and not a loose substring. The proxy base is service
//    config, so pinning `www.wixapis.com` would recognize the link in production only; but a bare
//    substring test would also accept `…/browse-session-live-notes` or somebody else's URL carrying the
//    words in a query param, and hand THAT to the button in place of the Maestro handoff.
const URL_TOKEN = /https?:\/\/[^\s<>"']+/gi;
const LIVE_VIEW_SEGMENT = /\/browse-session-live(\/|$)/i;

// A URL at the end of a sentence, or inside a markdown link/parenthesis, collects trailing characters
// that are punctuation rather than part of it. `?live=` means a real trailing `?`/`&`/`=` can be
// significant, so only unambiguous enders are trimmed.
const TRAILING_PUNCTUATION = /[).,;:!\]}>'"]+$/;

export function browseLiveViewUrl(text: string | undefined): string | null {
  if (!text) return null;
  for (const match of text.matchAll(URL_TOKEN)) {
    const url = match[0].replace(TRAILING_PUNCTUATION, '');
    // The segment test runs on the path ALONE — everything before the query — so the nested `?live=`
    // address cannot smuggle the words in and pass a link that only quotes ours.
    const path = url.split(/[?#]/)[0];
    if (LIVE_VIEW_SEGMENT.test(path)) return url;
  }
  return null;
}

/**
 * The action the task's text says is coming, or null for the default Maestro handoff.
 *
 * The three fields are read in the order the agent is most likely to have written the link: `summary` is
 * where `ManageTask` puts the parked reason verbatim, and the other two are read as a fallback so a link
 * the user can SEE on the screen is never one the button ignores.
 */
export function taskKnownAction(text: TaskActionText): TaskKnownAction | null {
  for (const field of [text.summary, text.currentAction, text.subtitle]) {
    const url = browseLiveViewUrl(field);
    if (url) return { kind: 'browseSession', url };
  }
  return null;
}
